'use client';
import React from 'react';
import { Modal, Box, Typography, Button } from '@mui/material';
import { IconSaxIcon } from '@/components/design-system/atoms/icon';
import { useTranslation } from 'react-i18next';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  url?: string;
}

export default function ShareModal({
  open,
  onClose,
  title = 'Share the house',
  url,
}: ShareModalProps) {
  const { t } = useTranslation();

  const handleShare = (platform: string) => {
    // Default to current URL if none provided
    const shareUrl = url || window.location.href;
    const encodedUrl = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(t('js.components.share_modal.share_text', { title }));

    let shareLink = '';

    switch (platform) {
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'x':
        shareLink = `https://x.com/i/flow/`;
        break;
      case 'whatsapp':
        shareLink = `https://api.whatsapp.com/send?text=${text}%20${encodedUrl}`;
        break;
      default:
        console.error('Unknown platform');
        return;
    }

    // Open in a new tab
    window.open(shareLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="share-modal-title"
      aria-describedby="share-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: {
            xs: '90%', // 90% width on mobile
            sm: 450, // 450px on tablet and up
          },
          maxWidth: 450, // Maximum width
          bgcolor: 'background.paper',
          borderRadius: 4,
          boxShadow: 3,
          p: {
            xs: 2, // Smaller padding on mobile
            sm: 4, // Original padding on tablet and up
          },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: {
            xs: 2, // Smaller gap on mobile
            sm: 3, // Original gap on tablet and up
          },
        }}
      >
        <div
          className="justify-start self-stretch font-sans font-bold leading-loose"
          style={{
            fontSize: '18px',
          }}
        >
          {t('js.components.share_modal.title')}
          <div className="justify-start self-stretch font-sans text-[14px] font-medium leading-loose text-N700">
            {t('js.components.share_modal.choose_platform')}
          </div>
        </div>

        <div className="flex justify-center gap-4">
          <button onClick={() => handleShare('facebook')}>
            <IconSaxIcon name="facebook" size={48} strokeColor="none" />
          </button>

          <button onClick={() => handleShare('x')}>
            <IconSaxIcon name="x" size={48} strokeColor="none" />
          </button>

          <button onClick={() => handleShare('whatsapp')}>
            <IconSaxIcon name="whatsapp" size={48} strokeColor="none" />
          </button>
        </div>

        <button
          type="button"
          className="flex items-center gap-2 rounded-[10px] px-3 py-2 text-gray-700 hover:bg-gray-100"
          style={{
            backgroundColor: '#F3F4F5',
          }}
          onClick={onClose}
        >
          <span className="text-[14px] font-bold">{t('js.components.share_modal.close')}</span>
        </button>
      </Box>
    </Modal>
  );
}
